import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Paperclip,
  Loader2,
  User,
  FileText,
  Download,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Clock,
  X,
  Eye,
} from "lucide-react";
import { AttachmentPreviewRow, Modal } from "./UI";
import {
  gapAttachmentPreviewPath,
  referenceEvidencePreviewPath,
  referenceEvidenceStatusLabel,
  REFERENCE_EVIDENCE_ADMIN_TITLE,
  REFERENCE_EVIDENCE_ADMIN_HINT,
  STORED_EVIDENCE_TABLE_COLUMN,
  STORED_EVIDENCE_TABLE_HINT,
  storedEvidenceStatusLabel,
} from "./gapRecommendationUtils";
import { openAuthenticatedAttachment } from "../../context/AppContext";

const EVIDENCE_STYLE_ID = "onedpdp-ai-evidence-styles-v12";
const PULSE_SESSION_KEY = "onedpdp_ai_evidence_pulse_dismissed";

function injectEvidenceStyles() {
  if (typeof document === "undefined") return;
  const existing = document.getElementById(EVIDENCE_STYLE_ID);
  if (existing) existing.remove();
  const style = document.createElement("style");
  style.id = EVIDENCE_STYLE_ID;
  style.textContent = `
    @keyframes aiEvidenceShimmer {
      0% { background-position: 200% center; }
      100% { background-position: -200% center; }
    }
    @keyframes aiEvidencePopIn {
      from { opacity: 0; transform: translateY(8px) scale(0.98); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }
    @keyframes aiEvidenceIconSoftPulse {
      0%, 100% { transform: scale(1); }
      50% { transform: scale(1.1); }
    }
    @keyframes aiEvidenceIconBlink {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.72; }
    }
    @keyframes aiEvidenceCheckBlink {
      0%, 100% { opacity: 1; }
      50% { opacity: 0.4; }
    }
    @keyframes aiEvidenceTooltipIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes aiEvidenceCheckIn {
      from { opacity: 0; transform: translateX(-6px); }
      to { opacity: 1; transform: translateX(0); }
    }

    .ai-evidence-trigger-wrap {
      display: inline-flex;
      vertical-align: middle;
      line-height: 0;
    }
    .ai-evidence-trigger-btn {
      padding: 0;
      margin: 0;
      border: none;
      background: transparent;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      line-height: 0;
      font-family: inherit;
      outline: none;
    }
    .ai-evidence-trigger-btn:focus-visible .ai-evidence-icon-mark {
      outline: 2px solid rgba(99, 102, 241, 0.45);
      outline-offset: 2px;
      border-radius: 4px;
    }
    .ai-evidence-icon-mark {
      display: inline-flex;
      line-height: 0;
      transform-origin: center center;
    }
    .ai-evidence-icon-mark.is-live {
      animation: aiEvidenceIconSoftPulse 2s ease-in-out infinite, aiEvidenceIconBlink 2.6s ease-in-out infinite;
    }
    .ai-evidence-icon-mark.is-attention {
      animation: aiEvidenceIconSoftPulse 1.4s ease-in-out infinite, aiEvidenceIconBlink 1.8s ease-in-out infinite;
    }
    .ai-evidence-icon-mark.is-live .evidence-check-dot {
      animation: aiEvidenceCheckBlink 1.4s ease-in-out infinite;
    }
    .ai-evidence-tooltip {
      position: fixed;
      z-index: 10001;
      width: max-content;
      max-width: min(92vw, 280px);
      padding: 6px 12px;
      border-radius: 999px;
      pointer-events: none;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(236, 253, 245, 0.96) 100%);
      border: 1px solid rgba(45, 212, 191, 0.55);
      box-shadow:
        0 10px 24px rgba(13, 148, 136, 0.16),
        0 2px 8px rgba(6, 182, 212, 0.08),
        inset 0 1px 0 rgba(255, 255, 255, 0.9);
      transform: translate(-50%, -100%);
      animation: aiEvidenceTooltipIn 0.16s ease-out;
    }
    .ai-evidence-tooltip-line {
      margin: 0;
      font-size: 10px;
      font-weight: 600;
      line-height: 1.3;
      color: #115e59;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .ai-evidence-tooltip-arrow {
      position: absolute;
      left: 50%;
      bottom: -5px;
      transform: translateX(-50%);
      width: 0;
      height: 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 5px solid rgba(236, 253, 245, 0.98);
      filter: drop-shadow(0 1px 0 rgba(45, 212, 191, 0.35));
    }

    .ai-evidence-popover-enter {
      animation: aiEvidencePopIn 0.32s cubic-bezier(0.22, 1, 0.36, 1);
    }
    .ai-evidence-popover-shell {
      position: relative;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      border-radius: 16px;
      border: 1px solid rgba(45, 212, 191, 0.28);
      background: linear-gradient(165deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 55%, rgba(236, 253, 245, 0.92) 100%);
      box-shadow:
        0 24px 48px rgba(15, 23, 42, 0.14),
        0 8px 20px rgba(13, 148, 136, 0.12),
        inset 0 1px 0 rgba(255, 255, 255, 0.9);
    }
    .ai-evidence-popover-shell::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 3px;
      background: linear-gradient(90deg, #0d9488, #06b6d4, #10b981, #0d9488);
      background-size: 200% auto;
      animation: aiEvidenceShimmer 4s linear infinite;
    }
    .ai-evidence-popover-body {
      padding: 13px 14px 12px;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
    .ai-evidence-popover-header {
      display: flex;
      align-items: flex-start;
      gap: 10px;
      margin-bottom: 10px;
      padding-bottom: 10px;
      border-bottom: 1px solid rgba(99, 102, 241, 0.1);
      flex-shrink: 0;
    }
    .ai-evidence-popover-icon {
      width: 36px;
      height: 36px;
      border-radius: 11px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      background: linear-gradient(135deg, #4f46e5 0%, #6366f1 55%, #10b981 100%);
      box-shadow: 0 6px 16px rgba(79, 70, 229, 0.28);
    }
    .ai-evidence-popover-title {
      margin: 0;
      font-size: 13px;
      font-weight: 800;
      letter-spacing: -0.02em;
      color: #0f172a;
      line-height: 1.3;
    }
    .ai-evidence-popover-sub {
      margin: 3px 0 0;
      font-size: 11px;
      color: #475569;
      line-height: 1.4;
    }
    .ai-evidence-status-pill {
      font-size: 9px;
      font-weight: 700;
      padding: 3px 8px;
      border-radius: 999px;
      white-space: nowrap;
      letter-spacing: 0.02em;
      text-transform: uppercase;
    }
    .ai-evidence-close-btn {
      border: none;
      background: rgba(148, 163, 184, 0.12);
      color: #64748b;
      cursor: pointer;
      padding: 4px;
      border-radius: 8px;
      display: flex;
      transition: background 0.15s ease, color 0.15s ease;
    }
    .ai-evidence-close-btn:hover {
      background: rgba(99, 102, 241, 0.12);
      color: #4338ca;
    }
    .ai-evidence-scroll {
      overflow-y: auto;
      padding-right: 4px;
      margin-bottom: 10px;
      scrollbar-width: thin;
      scrollbar-color: rgba(13, 148, 136, 0.4) transparent;
    }
    .ai-evidence-section-label {
      margin: 0 0 6px;
      font-size: 9px;
      font-weight: 800;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: #0f766e;
    }
    .ai-evidence-card-title {
      margin: 0 0 10px;
      padding: 9px 11px;
      border-radius: 10px;
      font-size: 12px;
      font-weight: 700;
      line-height: 1.5;
      color: #0f172a;
      background: linear-gradient(135deg, rgba(236, 253, 245, 0.98), rgba(204, 251, 241, 0.75));
      border: 1px solid rgba(45, 212, 191, 0.45);
      box-shadow: inset 3px 0 0 #0d9488;
    }
    .ai-evidence-highlight-block {
      margin: 0 0 10px;
      padding: 10px 11px;
      border-radius: 10px;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(236, 253, 245, 0.88));
      border: 1px solid rgba(45, 212, 191, 0.32);
      box-shadow: inset 3px 0 0 #14b8a6;
    }
    .ai-evidence-summary {
      margin: 0;
      font-size: 12px;
      line-height: 1.65;
      color: #334155;
      white-space: pre-wrap;
      word-break: break-word;
    }
    .ai-evidence-checklist-wrap {
      padding: 10px 11px;
      border-radius: 10px;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(240, 253, 250, 0.9));
      border: 1px solid rgba(45, 212, 191, 0.28);
      box-shadow: inset 3px 0 0 #10b981;
    }
    .ai-evidence-checklist {
      margin: 0;
      padding: 0;
      list-style: none;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    .ai-evidence-check-item {
      display: flex;
      gap: 8px;
      align-items: flex-start;
      padding: 7px 9px;
      border-radius: 8px;
      font-size: 11px;
      line-height: 1.5;
      color: #1e293b;
      background: rgba(255, 255, 255, 0.92);
      border: 1px solid rgba(167, 243, 208, 0.55);
      animation: aiEvidenceCheckIn 0.35s ease backwards;
    }
    .ai-evidence-check-icon {
      width: 16px;
      height: 16px;
      border-radius: 999px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      margin-top: 1px;
      background: linear-gradient(135deg, #d1fae5, #a7f3d0);
      color: #059669;
    }
    .ai-evidence-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 7px;
      flex-shrink: 0;
      padding-top: 2px;
    }
    .ai-evidence-btn-primary {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 7px 12px;
      border-radius: 10px;
      border: none;
      background: linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #7c3aed 100%);
      color: #fff;
      font-size: 10px;
      font-weight: 700;
      cursor: pointer;
      font-family: inherit;
      box-shadow: 0 4px 14px rgba(79, 70, 229, 0.28);
      transition: transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;
    }
    .ai-evidence-btn-primary:hover:not(:disabled) {
      transform: translateY(-1px);
      box-shadow: 0 6px 18px rgba(79, 70, 229, 0.35);
    }
    .ai-evidence-btn-primary:disabled {
      opacity: 0.55;
      cursor: not-allowed;
      transform: none;
      box-shadow: none;
    }
    .ai-evidence-btn-ghost {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 7px 10px;
      border-radius: 10px;
      border: 1px solid rgba(99, 102, 241, 0.35);
      background: rgba(255, 255, 255, 0.92);
      color: #4338ca;
      font-size: 10px;
      font-weight: 600;
      cursor: pointer;
      font-family: inherit;
      transition: background 0.18s ease, border-color 0.18s ease, transform 0.18s ease;
    }
    .ai-evidence-btn-ghost:hover {
      background: rgba(99, 102, 241, 0.08);
      border-color: rgba(99, 102, 241, 0.45);
      transform: translateY(-1px);
    }
    .ai-evidence-btn-icon {
      padding: 7px 8px;
    }
    .ai-evidence-footnote {
      margin: 8px 0 0;
      font-size: 10px;
      color: #64748b;
      line-height: 1.4;
    }
    .ai-evidence-shimmer-bar {
      background: linear-gradient(90deg, #6366f1 0%, #a855f7 35%, #10b981 70%, #6366f1 100%);
      background-size: 200% auto;
      animation: aiEvidenceShimmer 3s linear infinite;
    }
    .ai-evidence-loading {
      margin: 0;
      font-size: 10px;
      color: #64748b;
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 12px;
      border-radius: 10px;
      background: rgba(248, 250, 252, 0.9);
      border: 1px dashed rgba(148, 163, 184, 0.35);
    }
    .ai-evidence-error {
      margin: 0;
      font-size: 10px;
      color: #b91c1c;
      line-height: 1.45;
      padding: 10px 12px;
      border-radius: 10px;
      background: rgba(254, 242, 242, 0.9);
      border: 1px solid rgba(252, 165, 165, 0.45);
    }
  `;
  document.head.appendChild(style);
}

function allowAttachmentAction(onUseReference) {
  return typeof onUseReference === "function";
}

function getEvidenceTooltipCopy() {
  return "AI generated evidence · Click to view";
}

function useTooltipPosition(visible, anchorRef) {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const update = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos({ top: rect.top - 8, left: rect.left + rect.width / 2 });
  }, [anchorRef]);

  useEffect(() => {
    if (!visible) return undefined;
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [visible, update]);

  return pos;
}

function EvidenceIconTooltip({ visible, anchorRef, evidence, canAttach }) {
  const pos = useTooltipPosition(visible, anchorRef);
  if (!visible || !evidence) return null;
  const copy = getEvidenceTooltipCopy();
  return createPortal(
    <div
      className="ai-evidence-tooltip"
      role="tooltip"
      style={{ top: pos.top, left: pos.left }}
    >
      <p className="ai-evidence-tooltip-line">{copy}</p>
      <span className="ai-evidence-tooltip-arrow" aria-hidden="true" />
    </div>,
    document.body,
  );
}

function evidenceStatusMeta(status) {
  switch (status) {
    case "ready":
      return { color: "#059669", bg: "rgba(16,185,129,0.12)", border: "rgba(16,185,129,0.35)" };
    case "failed":
      return { color: "#dc2626", bg: "rgba(239,68,68,0.1)", border: "rgba(239,68,68,0.3)" };
    case "generating":
    case "pending":
      return { color: "#d97706", bg: "rgba(245,158,11,0.12)", border: "rgba(245,158,11,0.35)" };
    default:
      return { color: "#6366f1", bg: "rgba(99,102,241,0.1)", border: "rgba(99,102,241,0.25)" };
  }
}

const POPOVER_WIDTH = 380;

function popoverMaxHeight() {
  if (typeof window === "undefined") return 440;
  return Math.min(Math.round(window.innerHeight * 0.75), 520);
}

/** Distinct admin-template mark: document + verified badge */
function AdminTemplateIcon({ size = 16, accent = "#6366f1", light = false }) {
  const docFill = light ? "rgba(255,255,255,0.18)" : "#fff";
  const lineColor = light ? "#ffffff" : accent;
  const strokeColor = light ? "rgba(255,255,255,0.95)" : accent;
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <rect x="2.5" y="2" width="10.5" height="15" rx="1.8" fill={docFill} stroke={strokeColor} strokeWidth="1.5" />
      <path d="M6 6.5h5M6 9.5h5M6 12.5h3" stroke={lineColor} strokeWidth="1.1" strokeLinecap="round" />
      <circle className="evidence-check-dot" cx="15.2" cy="5.2" r="3.2" fill="#10b981" stroke="#fff" strokeWidth="1.2" />
      <path
        className="evidence-check-dot"
        d="M13.7 5.2l1 1 2.1-2.1"
        stroke="#fff"
        strokeWidth="1.15"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function usePopoverPosition(open, anchorRef) {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const update = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const margin = 10;
    const maxH = popoverMaxHeight();
    let top = rect.bottom + 8;
    let left = rect.left;

    if (top + maxH > window.innerHeight - margin) {
      top = Math.max(margin, rect.top - maxH - 8);
    }
    if (left + POPOVER_WIDTH > window.innerWidth - margin) {
      left = window.innerWidth - POPOVER_WIDTH - margin;
    }
    if (left < margin) left = margin;

    setPos({ top, left });
  }, [anchorRef]);

  useEffect(() => {
    if (!open) return undefined;
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, update]);

  return pos;
}

function AdminStatusIcon({ status }) {
  if (status === "ready") return <CheckCircle2 size={14} className="text-emerald-600" />;
  if (status === "failed") return <AlertCircle size={14} className="text-red-500" />;
  return <Clock size={14} className="text-amber-500 animate-pulse" />;
}

function catalogStatusLabel(status) {
  if (status === "not_started") return "Not generated";
  return referenceEvidenceStatusLabel(status);
}

function catalogStatusMeta(status) {
  if (status === "not_started") {
    return { color: "#64748b", bg: "rgba(148,163,184,0.12)", border: "rgba(148,163,184,0.3)" };
  }
  return evidenceStatusMeta(status);
}

export function buildEvidenceCatalogMap(items = []) {
  const map = {};
  for (const item of items) {
    if (item.question_key) map[item.question_key] = item;
    if (item.question_uuid) map[item.question_uuid] = item;
    if (item.display_id) map[item.display_id] = item;
  }
  return map;
}

export function lookupEvidenceCatalogItem(row, map) {
  if (!row || !map) return null;
  return map[row.external_id] || map[row.id] || null;
}

function storedStatusMeta(status) {
  switch (status) {
    case "ready":
      return { color: "#047857", bg: "#ecfdf5", border: "#a7f3d0" };
    case "generating":
    case "pending":
      return { color: "#b45309", bg: "#fffbeb", border: "#fde68a" };
    case "failed":
      return { color: "#b91c1c", bg: "#fef2f2", border: "#fecaca" };
    case "not_started":
    default:
      return { color: "#64748b", bg: "#f8fafc", border: "#e2e8f0" };
  }
}

export function ReferenceEvidenceStatusBadge({
  status = "not_started",
  compact = false,
  mode = "admin",
}) {
  useEffect(() => {
    injectEvidenceStyles();
  }, []);
  const meta = mode === "stored" ? storedStatusMeta(status) : catalogStatusMeta(status);
  const label = mode === "stored" ? storedEvidenceStatusLabel(status) : catalogStatusLabel(status);
  return (
    <span
      className={`inline-flex items-center rounded-full font-semibold ${
        compact ? "text-[10px] px-2 py-0.5" : "text-[11px] px-2.5 py-1"
      }`}
      style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }}
    >
      {label}
    </span>
  );
}

export function ReferenceEvidenceTableCell({
  catalogItem,
  onView,
  loading = false,
}) {
  const status = catalogItem?.generation_status || "not_started";
  const evidence = catalogItem?.evidence;
  const title = evidence?.title?.trim();
  const label =
    status === "ready"
      ? title || evidence?.file_name || "Evidence file"
      : status === "generating" || status === "pending"
        ? "Updating…"
        : status === "failed"
          ? "Evidence unavailable"
          : "No evidence";
  const canView = typeof onView === "function";

  return (
    <button
      type="button"
      onClick={onView}
      disabled={!canView || loading}
      className="group flex w-full min-w-[8.5rem] items-center gap-2 rounded-lg border border-slate-200/80 bg-slate-50/70 px-2.5 py-2 text-left transition-colors hover:border-slate-300 hover:bg-white disabled:opacity-60"
      title={STORED_EVIDENCE_TABLE_HINT}
      aria-label={`View stored evidence: ${label}`}
    >
      {loading ? (
        <Loader2 size={14} className="shrink-0 animate-spin text-slate-400" />
      ) : (
        <Eye
          size={15}
          className="shrink-0 text-slate-400 transition-colors group-hover:text-indigo-600"
        />
      )}
      <span className="min-w-0 flex-1 text-[11px] leading-snug text-slate-700 line-clamp-2 group-hover:text-indigo-700">
        {loading ? "Loading…" : label}
      </span>
    </button>
  );
}

/** Modal view for stored evidence (admin questions table). */
export function ReferenceEvidenceViewModal({
  open,
  onClose,
  questionLabel = "",
  catalogItem,
  loading = false,
  regenerating = false,
  onRegenerate,
  pendingSaveHint,
}) {
  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={questionLabel ? `Stored evidence — ${questionLabel}` : "Stored evidence"}
      size="lg"
    >
      <ReferenceEvidenceExpandPanel
        catalogItem={catalogItem}
        loading={loading}
        regenerating={regenerating}
        onRegenerate={onRegenerate}
        pendingSaveHint={pendingSaveHint}
      />
    </Modal>
  );
}

/** Nested detail row under a question in the admin table. */
export function ReferenceEvidenceExpandPanel({
  catalogItem,
  loading = false,
  regenerating = false,
  onRegenerate,
  onPreview,
  pendingSaveHint = "No stored evidence yet for this question.",
}) {
  useEffect(() => {
    injectEvidenceStyles();
  }, []);

  const status = catalogItem?.generation_status || "not_started";
  const evidence = catalogItem?.evidence;
  const canRegenerate = !!catalogItem?.question_uuid && typeof onRegenerate === "function";
  const preview = onPreview || (evidence?.has_file
    ? () => openAuthenticatedAttachment(referenceEvidencePreviewPath(evidence))
    : null);

  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-slate-50/80 px-4 py-2.5">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-slate-800">Stored evidence for this question</p>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {evidence?.file_name || "Evidence shown to users while they answer this question."}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ReferenceEvidenceStatusBadge status={status} mode="stored" compact />
          {preview && evidence?.has_file && (
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
              onClick={preview}
            >
              <FileText size={12} />
              View file
            </button>
          )}
          {canRegenerate && (
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              disabled={regenerating}
              onClick={onRegenerate}
            >
              <RefreshCw size={12} className={regenerating ? "animate-spin" : ""} />
              Regenerate
            </button>
          )}
        </div>
      </div>

      <div className="px-4 py-3 space-y-3">
        {loading ? (
          <p className="flex items-center gap-2 text-xs text-slate-500">
            <Loader2 size={14} className="animate-spin" />
            Loading stored evidence…
          </p>
        ) : status === "not_started" ? (
          <p className="text-xs text-slate-600 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-2.5">
            {pendingSaveHint}
          </p>
        ) : status === "ready" && evidence ? (
          <>
            {evidence.title && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Title</p>
                <p className="text-sm font-medium text-slate-800">{evidence.title}</p>
              </div>
            )}
            {evidence.summary && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Summary</p>
                <p className="text-xs leading-relaxed text-slate-600 whitespace-pre-wrap">{evidence.summary}</p>
              </div>
            )}
            {(evidence.checklist || []).length > 0 && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Checklist</p>
                <ul className="space-y-1.5">
                  {(evidence.checklist || []).map((item) => (
                    <li key={item} className="flex gap-2 text-xs text-slate-700">
                      <CheckCircle2 size={13} className="shrink-0 text-emerald-600 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500 pt-1 border-t border-slate-100">
              {evidence.generated_at && (
                <span>Last updated {new Date(evidence.generated_at).toLocaleString()}</span>
              )}
              {evidence.file_size_bytes ? (
                <span>{Math.max(1, Math.round(evidence.file_size_bytes / 1024))} KB file</span>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <p className="text-xs text-slate-600">
              {status === "failed"
                ? "Stored evidence could not be prepared."
                : "Evidence is being updated. Check back shortly."}
            </p>
            {evidence?.error_message && (
              <p className="text-xs text-red-600 rounded-md bg-red-50 border border-red-100 px-3 py-2">
                {evidence.error_message}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}


/** Premium admin card for create/edit question modals. */
export function ReferenceEvidenceAdminCard({
  evidence,
  loading = false,
  regenerating = false,
  onRegenerate,
  onPreview,
  audienceLabel = "users",
  pendingSaveHint = "Save the question first. New questions generate evidence automatically. On edit, click Regenerate only if you want a new template.",
}) {
  useEffect(() => {
    injectEvidenceStyles();
  }, []);

  const status = evidence?.generation_status;
  const meta = evidenceStatusMeta(status);

  return (
    <div className="relative overflow-hidden rounded-2xl border border-indigo-100/80 bg-gradient-to-br from-white via-slate-50/90 to-indigo-50/50 shadow-[0_8px_30px_rgba(99,102,241,0.08)]">
      <div className="ai-evidence-shimmer-bar h-1 w-full" />
      <div className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="ai-evidence-popover-icon h-11 w-11 shrink-0">
              <AdminTemplateIcon size={20} light />
            </div>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-bold text-slate-900 tracking-tight">{REFERENCE_EVIDENCE_ADMIN_TITLE}</p>
                <span className="inline-flex items-center rounded-full bg-gradient-to-r from-indigo-100 to-emerald-50 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-indigo-700 border border-indigo-100">
                  Admin → {audienceLabel}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">{REFERENCE_EVIDENCE_ADMIN_HINT}</p>
            </div>
          </div>
          {onRegenerate && (
            <button
              type="button"
              onClick={onRegenerate}
              disabled={regenerating}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-slate-200/90 bg-white/90 px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-white hover:shadow-md disabled:opacity-60 transition-all"
            >
              <RefreshCw size={12} className={regenerating ? "animate-spin" : ""} />
              Regenerate
            </button>
          )}
        </div>

        {!onRegenerate ? (
          <div className="rounded-xl border border-dashed border-indigo-200/80 bg-indigo-50/40 px-3 py-3 text-xs text-indigo-800">
            {pendingSaveHint}
          </div>
        ) : loading && !evidence ? (
          <p className="ai-evidence-loading">
            <Loader2 size={14} className="animate-spin" />
            Loading generation status…
          </p>
        ) : (
          <div
            className="rounded-xl border px-3 py-3 space-y-3 backdrop-blur-sm"
            style={{ background: meta.bg, borderColor: meta.border }}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <AdminStatusIcon status={status} />
              <span className="ai-evidence-status-pill" style={{ background: "rgba(255,255,255,0.75)", color: meta.color, border: `1px solid ${meta.border}` }}>
                {referenceEvidenceStatusLabel(status)}
              </span>
              {evidence?.title && (
                <span className="text-xs font-semibold text-slate-700">{evidence.title}</span>
              )}
            </div>
            {evidence?.summary && (
              <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-wrap rounded-lg bg-white/70 border border-white/80 px-3 py-2">
                {evidence.summary}
              </p>
            )}
            {(evidence?.checklist || []).length > 0 && (
              <ul className="ai-evidence-checklist">
                {(evidence.checklist || []).map((item, index) => (
                  <li
                    key={item}
                    className="ai-evidence-check-item"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <span className="ai-evidence-check-icon">
                      <CheckCircle2 size={9} />
                    </span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
            {evidence?.error_message && (
              <p className="ai-evidence-error">{evidence.error_message}</p>
            )}
            {evidence?.has_file && onPreview && (
              <button
                type="button"
                onClick={onPreview}
                className="ai-evidence-btn-ghost"
              >
                <FileText size={12} />
                Preview admin template PDF
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SuggestedEvidencePopoverContent({
  evidence,
  usingReference = false,
  onUseReference,
  disabled = false,
  attachDisabledReason = "",
  previewError,
  onPreview,
  onClose,
}) {
  const status = evidence.generation_status;
  const previewPath = referenceEvidencePreviewPath(evidence);
  const meta = evidenceStatusMeta(status);
  const checklist = evidence.checklist || [];
  const blockReason = usingReference
    ? ""
    : !allowAttachmentAction(onUseReference)
      ? "Use as evidence is not available on this screen."
      : attachDisabledReason
        || (disabled ? "This assessment is locked or unavailable for edits." : "");
  const canUseEvidence =
    allowAttachmentAction(onUseReference) &&
    !disabled &&
    !attachDisabledReason &&
    !usingReference;

  const handleUseEvidence = async () => {
    if (!allowAttachmentAction(onUseReference) || usingReference || disabled || attachDisabledReason) return;
    try {
      await onUseReference(evidence);
      onClose?.();
    } catch {
      // Parent surfaces errors (toast / banner).
    }
  };

  return (
    <div className="ai-evidence-popover-body">
      <div className="ai-evidence-popover-header">
        <div className="ai-evidence-popover-icon">
          <AdminTemplateIcon size={17} light />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p className="ai-evidence-popover-title">Admin template</p>
          <p className="ai-evidence-popover-sub">AI-prepared evidence guide from your tenant admin</p>
        </div>
        <span
          className="ai-evidence-status-pill"
          style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }}
        >
          {referenceEvidenceStatusLabel(status)}
        </span>
        {onClose && (
          <button type="button" onClick={onClose} aria-label="Close" className="ai-evidence-close-btn">
            <X size={12} />
          </button>
        )}
      </div>

      {status === "ready" && (
        <>
          <div
            className="ai-evidence-scroll"
            style={{ maxHeight: Math.max(220, popoverMaxHeight() - 152) }}
          >
            {evidence.title && <p className="ai-evidence-card-title">{evidence.title}</p>}
            {evidence.summary && (
              <div className="ai-evidence-highlight-block">
                <p className="ai-evidence-section-label">Overview</p>
                <p className="ai-evidence-summary">{evidence.summary}</p>
              </div>
            )}
            {checklist.length > 0 && (
              <div className="ai-evidence-checklist-wrap">
                <p className="ai-evidence-section-label">Key proof points</p>
                <ul className="ai-evidence-checklist">
                  {checklist.map((item, index) => (
                    <li
                      key={item}
                      className="ai-evidence-check-item"
                      style={{ animationDelay: `${index * 0.06}s` }}
                    >
                      <span className="ai-evidence-check-icon">
                        <CheckCircle2 size={10} />
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="ai-evidence-actions">
            <button
              type="button"
              className="ai-evidence-btn-primary"
              disabled={!canUseEvidence || usingReference}
              onClick={handleUseEvidence}
            >
              {usingReference ? "Attaching…" : "Use as evidence"}
            </button>
            {previewPath && (
              <>
                <button type="button" className="ai-evidence-btn-ghost" onClick={() => onPreview(previewPath)}>
                  <FileText size={11} /> View PDF
                </button>
                <button
                  type="button"
                  className="ai-evidence-btn-ghost ai-evidence-btn-icon"
                  onClick={() => onPreview(previewPath, { inline: false })}
                  title="Download PDF"
                >
                  <Download size={11} />
                </button>
              </>
            )}
          </div>
          {blockReason && !usingReference && (
            <p className="ai-evidence-footnote" style={{ color: "#b45309" }}>{blockReason}</p>
          )}
          {!blockReason && (
            <p className="ai-evidence-footnote">Scroll for full text · open PDF for complete document</p>
          )}
          {previewError && <p className="ai-evidence-error" style={{ marginTop: 8 }}>{previewError}</p>}
        </>
      )}

      {(status === "pending" || status === "generating") && (
        <p className="ai-evidence-loading">
          <Loader2 size={12} className="animate-spin" />
          Admin is preparing this template…
        </p>
      )}

      {status === "failed" && (
        <p className="ai-evidence-error">
          {evidence.error_message || "Template unavailable. Contact your admin."}
        </p>
      )}
    </div>
  );
}

/** Tiny admin-template icon — click opens a compact floating popover (portal). */
export function SuggestedEvidenceTrigger({
  evidence,
  usingReference = false,
  onUseReference,
  disabled = false,
  attachDisabledReason = "",
  className = "",
  style = {},
}) {
  const [open, setOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [showPulse, setShowPulse] = useState(false);
  const anchorRef = useRef(null);
  const popoverPos = usePopoverPosition(open, anchorRef);

  useEffect(() => {
    injectEvidenceStyles();
  }, []);

  useEffect(() => {
    if (!evidence) return;
    const dismissed = sessionStorage.getItem(PULSE_SESSION_KEY);
    if (evidence.generation_status === "ready" && !dismissed) {
      setShowPulse(true);
    }
  }, [evidence]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (anchorRef.current?.contains(e.target)) return;
      const pop = document.getElementById("ai-evidence-popover-root");
      if (pop?.contains(e.target)) return;
      setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const openPreview = async (path, { inline = true } = {}) => {
    setPreviewError("");
    try {
      await openAuthenticatedAttachment(path, { inline });
    } catch (err) {
      setPreviewError(err.message || "Could not open attachment");
    }
  };

  const handleOpen = (e) => {
    e.stopPropagation();
    setShowPulse(false);
    sessionStorage.setItem(PULSE_SESSION_KEY, "1");
    setOpen((v) => !v);
  };

  if (!evidence) return null;

  const status = evidence.generation_status;
  const meta = evidenceStatusMeta(status);
  const isReady = status === "ready";

  const popover =
    open &&
    createPortal(
      <div
        id="ai-evidence-popover-root"
        role="dialog"
        aria-label="Admin template"
        className="ai-evidence-popover-enter"
        style={{
          position: "fixed",
          top: popoverPos.top,
          left: popoverPos.left,
          zIndex: 10000,
          width: POPOVER_WIDTH,
          maxHeight: popoverMaxHeight(),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="ai-evidence-popover-shell" style={{ maxHeight: popoverMaxHeight(), display: "flex", flexDirection: "column" }}>
          <SuggestedEvidencePopoverContent
            evidence={evidence}
            usingReference={usingReference}
            onUseReference={onUseReference}
            disabled={disabled}
            attachDisabledReason={attachDisabledReason}
            previewError={previewError}
            onPreview={openPreview}
            onClose={() => setOpen(false)}
          />
        </div>
      </div>,
      document.body,
    );

  return (
    <>
      <span ref={anchorRef} className={["ai-evidence-trigger-wrap", className].filter(Boolean).join(" ")} style={style}>
        <button
          type="button"
          aria-label="AI evidence template"
          aria-expanded={open}
          onClick={handleOpen}
          onMouseEnter={() => setTooltipOpen(true)}
          onMouseLeave={() => setTooltipOpen(false)}
          onFocus={() => setTooltipOpen(true)}
          onBlur={() => setTooltipOpen(false)}
          className="ai-evidence-trigger-btn"
        >
          <span
            className={[
              "ai-evidence-icon-mark",
              isReady ? "is-live" : "",
              isReady && showPulse ? "is-attention" : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <AdminTemplateIcon size={17} accent={isReady ? "#059669" : meta.color} />
          </span>
        </button>
      </span>
      <EvidenceIconTooltip
        visible={tooltipOpen && !open}
        anchorRef={anchorRef}
        evidence={evidence}
        canAttach={allowAttachmentAction(onUseReference)}
      />
      {popover}
    </>
  );
}

/** @deprecated — use SuggestedEvidenceTrigger */
export function SuggestedEvidencePanel(props) {
  return <SuggestedEvidenceTrigger {...props} />;
}

export default function GapQuestionComment({
  questionId,
  note = "",
  owner = "",
  onNoteChange,
  onOwnerChange,
  attachments = [],
  uploading = false,
  onUpload,
  onRemove,
  removingAttachmentId = null,
  allowAttachment = false,
  attachmentRequired = false,
  disabled = false,
}) {
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !onUpload) return;
    try {
      await onUpload(questionId, file);
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div
      style={{
        marginTop: 12,
        paddingTop: 12,
        borderTop: "1px solid rgba(99,102,241,0.1)",
      }}
    >
      <label
        style={{
          display: "block",
          fontSize: 10,
          fontWeight: 700,
          color: "#475569",
          marginBottom: 6,
          letterSpacing: "0.02em",
          textTransform: "uppercase",
        }}
      >
        Comments & evidence
      </label>

      <textarea
        value={note}
        onChange={(e) => onNoteChange?.(e.target.value)}
        placeholder="Add comments, evidence notes, or observations…"
        rows={3}
        disabled={disabled}
        style={{
          width: "100%",
          resize: "vertical",
          minHeight: 72,
          padding: "10px 12px",
          borderRadius: 10,
          border: "1px solid rgba(99,102,241,0.15)",
          background: "#f8fafc",
          color: "#0f172a",
          fontSize: 12,
          lineHeight: 1.5,
          fontFamily: "inherit",
          outline: "none",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = "rgba(99,102,241,0.45)";
          e.target.style.boxShadow = "0 0 0 3px rgba(99,102,241,0.12)";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "rgba(99,102,241,0.15)";
          e.target.style.boxShadow = "none";
        }}
      />

      {allowAttachment && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 8,
            flexWrap: "wrap",
          }}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.webp,.gif,.csv,.zip,.txt"
            style={{ display: "none" }}
            onChange={handleFile}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading || disabled}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              borderRadius: 8,
              border: attachmentRequired ? "1.5px dashed #f59e0b" : "1px solid rgba(99,102,241,0.2)",
              background: attachmentRequired ? "#fffbeb" : "#ffffff",
              color: attachmentRequired ? "#92400e" : "#6366f1",
              fontSize: 11,
              fontWeight: 600,
              cursor: uploading || disabled ? "not-allowed" : "pointer",
              opacity: uploading || disabled ? 0.7 : 1,
              fontFamily: "inherit",
            }}
          >
            {uploading ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <Paperclip size={13} />
            )}
            {attachmentRequired ? "Attach required file *" : "Attach file"}
          </button>
          <span style={{ fontSize: 10, color: "#94a3b8" }}>PDF, Office, images, CSV, ZIP</span>
        </div>
      )}

      {attachments.length > 0 && (
        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
          {attachments.map((file) => (
            <AttachmentPreviewRow
              key={file.id}
              file={file}
              previewPath={gapAttachmentPreviewPath(file)}
              onRemove={disabled ? undefined : () => onRemove?.(questionId, file)}
              removing={removingAttachmentId === file.id}
            />
          ))}
        </div>
      )}

      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 5,
          fontSize: 10,
          fontWeight: 700,
          color: "#475569",
          marginTop: 10,
          marginBottom: 6,
          letterSpacing: "0.02em",
          textTransform: "uppercase",
        }}
      >
        <User size={11} />
        Responsible owner
      </label>
      <input
        type="text"
        value={owner}
        onChange={(e) => onOwnerChange?.(e.target.value)}
        placeholder="Name or role responsible for this item"
        disabled={disabled}
        style={{
          width: "100%",
          padding: "8px 12px",
          borderRadius: 10,
          border: "1px solid rgba(99,102,241,0.15)",
          background: "#f8fafc",
          color: "#0f172a",
          fontSize: 12,
          fontFamily: "inherit",
          outline: "none",
        }}
        onFocus={(e) => {
          e.target.style.borderColor = "rgba(99,102,241,0.45)";
          e.target.style.boxShadow = "0 0 0 3px rgba(99,102,241,0.12)";
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "rgba(99,102,241,0.15)";
          e.target.style.boxShadow = "none";
        }}
      />
    </div>
  );
}
